import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Clock } from 'lucide-react';
import { attractions } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';

const AttractionCard = ({ attraction, translatedAttraction }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const nextImage = (e) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev + 1) % attraction.images.length);
  };

  const prevImage = (e) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev - 1 + attraction.images.length) % attraction.images.length);
  };

  // Use translated content if available, otherwise fall back to original
  const name = translatedAttraction?.name || attraction.name;
  const description = translatedAttraction?.description || attraction.description;
  const distance = translatedAttraction?.distance || attraction.distance;

  return (
    <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 group">
      {/* Image Carousel */}
      <div className="relative h-56 overflow-hidden">
        {attraction.images.map((img, index) => (
          <img
            key={index}
            src={img}
            alt={`${name} - ${index + 1}`}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${
              index === currentImageIndex ? 'opacity-100' : 'opacity-0'
            }`}
          />
        ))}
        
        {/* Navigation */}
        <button
          onClick={prevImage}
          className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"
        >
          <ChevronLeft size={20} />
        </button>
        <button
          onClick={nextImage}
          className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"
        >
          <ChevronRight size={20} />
        </button>

        {/* Image Indicators */}
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
          {attraction.images.map((_, index) => (
            <div
              key={index}
              className={`w-2 h-2 rounded-full transition-colors duration-300 ${
                index === currentImageIndex ? 'bg-white' : 'bg-white/50'
              }`}
            />
          ))}
        </div>

        {/* Distance Badge */}
        <div className="absolute top-3 left-3 bg-amber-700 text-white px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1">
          <Clock size={12} />
          {distance}
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-3">{name}</h3>
        <p className="text-gray-600 text-sm leading-relaxed">{description}</p>
      </div>
    </div>
  );
};

const Attractions = () => {
  const { t } = useLanguage();

  // Map attraction IDs to translation keys
  const attractionKeyMap = {
    1: 'bascarsija',
    2: 'interfaith',
    3: 'vijecnica',
    4: 'trebevic',
    5: 'vrelobosne',
    6: 'latinbridge',
    7: 'nature',
  };

  return (
    <section id="atrakcije" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <p className="text-amber-700 text-sm font-medium tracking-wider mb-2">{t.attractions.subtitle}</p>
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-gray-900 mb-4">
            {t.attractions.title}
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            {t.attractions.description}
          </p>
        </div>

        {/* Attractions Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {attractions.map((attraction) => (
            <AttractionCard 
              key={attraction.id} 
              attraction={attraction}
              translatedAttraction={t.attractions.items[attractionKeyMap[attraction.id]]}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Attractions;
